import { useState, useEffect, useCallback, useMemo } from "react";
import { crApi } from "../lib/crScope";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Download, FileSpreadsheet, RefreshCw, Clock, User, FileText, Inbox, Loader2, Trash2,
  ChevronDown, ChevronRight, Search,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const formatFecha = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-DO", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const formatTamano = (bytes) => {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export default function HistorialReporte({ API_URL, getAuthHeaders, currentUser, refreshKey }) {
  const [reportes, setReportes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [descargandoId, setDescargandoId] = useState(null);
  const [puedeEliminar, setPuedeEliminar] = useState(false);
  const [eliminandoId, setEliminandoId] = useState(null);
  const [confirmReporte, setConfirmReporte] = useState(null);
  // Filtros
  const [filtroTexto, setFiltroTexto] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [filtroUsuario, setFiltroUsuario] = useState("todos");
  const [filtroDesde, setFiltroDesde] = useState("");
  const [filtroHasta, setFiltroHasta] = useState("");
  // Grupos expandidos (por nombre de usuario)
  const [gruposAbiertos, setGruposAbiertos] = useState({});

  const fetchReportes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}${crApi()}/control-renovaciones/reportes-historial`, getAuthHeaders());
      if (!res.ok) throw new Error("Error al cargar el historial");
      const data = await res.json();
      setReportes(data.reportes || []);
      setPuedeEliminar(!!data.puede_eliminar);
    } catch (e) {
      toast.error("No se pudo cargar el historial de reportes");
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeaders]);

  useEffect(() => {
    fetchReportes();
  }, [fetchReportes, refreshKey]);

  const usuariosUnicos = useMemo(
    () => [...new Set(reportes.map((r) => r.usuario_nombre || "—"))].sort((a, b) => a.localeCompare(b)),
    [reportes]
  );

  const reportesFiltrados = useMemo(() => {
    const q = filtroTexto.trim().toLowerCase();
    return reportes.filter((r) => {
      if (filtroTipo !== "todos" && (r.tipo || "") !== filtroTipo) return false;
      if (filtroUsuario !== "todos" && (r.usuario_nombre || "—") !== filtroUsuario) return false;
      const fecha = (r.created_at || "").slice(0, 10);
      if (filtroDesde && fecha < filtroDesde) return false;
      if (filtroHasta && fecha > filtroHasta) return false;
      if (q) {
        const tipoLabel = r.tipo === "corporativo" ? "corporativo" : "individual/pymes";
        const haystack = `${r.filename || ""} ${r.usuario_nombre || ""} ${tipoLabel} ${formatFecha(r.created_at)}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [reportes, filtroTexto, filtroTipo, filtroUsuario, filtroDesde, filtroHasta]);

  const grupos = useMemo(() => {
    const map = new Map();
    for (const r of reportesFiltrados) {
      const key = r.usuario_nombre || "—";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [reportesFiltrados]);

  const hayFiltrosActivos = filtroTexto || filtroTipo !== "todos" || filtroUsuario !== "todos" || filtroDesde || filtroHasta;

  const toggleGrupo = (nombre) => setGruposAbiertos((prev) => ({ ...prev, [nombre]: !prev[nombre] }));
  const grupoAbierto = (nombre) => hayFiltrosActivos ? gruposAbiertos[nombre] !== false : !!gruposAbiertos[nombre];

  const limpiarFiltros = () => {
    setFiltroTexto(""); setFiltroTipo("todos"); setFiltroUsuario("todos"); setFiltroDesde(""); setFiltroHasta("");
  };

  const handleDescargar = async (reporte) => {
    setDescargandoId(reporte.id);
    try {
      const res = await fetch(
        `${API_URL}${crApi()}/control-renovaciones/reportes-historial/${reporte.id}/download`,
        getAuthHeaders()
      );
      if (!res.ok) throw new Error("No se pudo descargar el reporte");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = reporte.filename || "reporte.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo descargar el reporte");
    } finally {
      setDescargandoId(null);
    }
  };

  const handleEliminar = async () => {
    if (!confirmReporte) return;
    const reporte = confirmReporte;
    setEliminandoId(reporte.id);
    try {
      const res = await fetch(
        `${API_URL}${crApi()}/control-renovaciones/reportes-historial/${reporte.id}`,
        { ...getAuthHeaders(), method: "DELETE" }
      );
      if (!res.ok) throw new Error("No se pudo eliminar el reporte");
      toast.success("Reporte eliminado del historial");
      setReportes((prev) => prev.filter((r) => r.id !== reporte.id));
    } catch (e) {
      toast.error("No se pudo eliminar el reporte");
    } finally {
      setEliminandoId(null);
      setConfirmReporte(null);
    }
  };

  const renderTabla = (items) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="reportes-table">
        <thead>
          <tr className="border-b text-left text-slate-500">
            <th className="py-2 px-3 font-medium"><Clock className="w-4 h-4 inline mr-1" />Fecha</th>
            <th className="py-2 px-3 font-medium">Tipo</th>
            <th className="py-2 px-3 font-medium">Archivo</th>
            <th className="py-2 px-3 font-medium text-right">Registros</th>
            <th className="py-2 px-3 font-medium text-right">Tamaño</th>
            <th className="py-2 px-3 font-medium text-center">Descargar</th>
            {puedeEliminar && (
              <th className="py-2 px-3 font-medium text-center">Eliminar</th>
            )}
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`reporte-row-${r.id}`}>
              <td className="py-2 px-3 whitespace-nowrap">{formatFecha(r.created_at)}</td>
              <td className="py-2 px-3">
                <Badge variant={r.tipo === "corporativo" ? "default" : "secondary"}>
                  {r.tipo === "corporativo" ? "Corporativo" : "Individual/Pymes"}
                </Badge>
              </td>
              <td className="py-2 px-3">
                <span className="flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                  <span className="truncate max-w-[260px]" title={r.filename}>{r.filename}</span>
                </span>
              </td>
              <td className="py-2 px-3 text-right">{r.total_registros}</td>
              <td className="py-2 px-3 text-right whitespace-nowrap">{formatTamano(r.tamano_bytes)}</td>
              <td className="py-2 px-3 text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDescargar(r)}
                  disabled={descargandoId === r.id}
                  data-testid={`reporte-download-${r.id}`}
                >
                  {descargandoId === r.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </Button>
              </td>
              {puedeEliminar && (
                <td className="py-2 px-3 text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setConfirmReporte(r)}
                    disabled={eliminandoId === r.id}
                    data-testid={`reporte-delete-${r.id}`}
                  >
                    {eliminandoId === r.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="historial-reporte-tab">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-blue-600" />
              Historial de Reporte
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Registro de los reportes Excel generados (auditoría), según tu nivel de visibilidad. Agrupados por usuario.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchReportes}
            data-testid="reportes-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </CardHeader>
        <CardContent>
          {/* Filtros */}
          <div className="flex flex-wrap items-end gap-3 mb-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs text-slate-500">Buscar</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={filtroTexto}
                  onChange={(e) => setFiltroTexto(e.target.value)}
                  placeholder="Ejecutivo, archivo, tipo..."
                  className="h-9 pl-8"
                  data-testid="reportes-filtro-texto"
                />
              </div>
            </div>
            <div className="w-44">
              <Label className="text-xs text-slate-500">Ejecutivo</Label>
              <Select value={filtroUsuario} onValueChange={setFiltroUsuario}>
                <SelectTrigger className="h-9" data-testid="reportes-filtro-usuario"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {usuariosUnicos.map((u) => (
                    <SelectItem key={u} value={u}>{u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="text-xs text-slate-500">Tipo</Label>
              <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                <SelectTrigger className="h-9" data-testid="reportes-filtro-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="corporativo">Corporativo</SelectItem>
                  <SelectItem value="individual">Individual/Pymes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-36">
              <Label className="text-xs text-slate-500">Desde</Label>
              <Input type="date" value={filtroDesde} onChange={(e) => setFiltroDesde(e.target.value)} className="h-9" data-testid="reportes-filtro-desde" />
            </div>
            <div className="w-36">
              <Label className="text-xs text-slate-500">Hasta</Label>
              <Input type="date" value={filtroHasta} onChange={(e) => setFiltroHasta(e.target.value)} className="h-9" data-testid="reportes-filtro-hasta" />
            </div>
            {hayFiltrosActivos && (
              <Button variant="outline" size="sm" className="h-9" onClick={limpiarFiltros} data-testid="reportes-filtro-limpiar">
                Limpiar
              </Button>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Cargando historial...
            </div>
          ) : reportes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400" data-testid="reportes-empty">
              <Inbox className="w-10 h-10 mb-3" />
              <p className="text-sm">Aún no has generado reportes.</p>
              <p className="text-xs mt-1">Cuando exportes un Excel, se guardará aquí automáticamente.</p>
            </div>
          ) : reportesFiltrados.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400" data-testid="reportes-sin-resultados">
              <Inbox className="w-10 h-10 mb-3" />
              <p className="text-sm">Ningún reporte coincide con los filtros.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {grupos.map(([nombre, items]) => (
                <div key={nombre} className="border rounded-lg overflow-hidden" data-testid={`reportes-grupo-${nombre}`}>
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-left"
                    onClick={() => toggleGrupo(nombre)}
                    data-testid={`reportes-grupo-toggle-${nombre}`}
                  >
                    <span className="flex items-center gap-2 font-medium">
                      {grupoAbierto(nombre) ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                      <User className="w-4 h-4 text-blue-600" />
                      {nombre}
                    </span>
                    <span className="flex items-center gap-3 text-xs text-slate-500">
                      <span>Último: {formatFecha(items[0]?.created_at)}</span>
                      <Badge variant="secondary">{items.length} reporte{items.length !== 1 ? "s" : ""}</Badge>
                    </span>
                  </button>
                  {grupoAbierto(nombre) && renderTabla(items)}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmReporte} onOpenChange={(o) => !o && setConfirmReporte(null)}>
        <AlertDialogContent data-testid="reporte-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este reporte?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará permanentemente el archivo{" "}
              <span className="font-medium">{confirmReporte?.filename}</span> del historial.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reporte-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleEliminar}
              className="bg-red-600 hover:bg-red-700"
              data-testid="reporte-delete-confirm"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
